import { LogOut } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ConfirmDialog, LanguageSwitcher, ThemeToggle } from "@/components/shared";
import { useLogout } from "@/hooks";
import { useSessionStore } from "@/store";

import { NotificationBell } from "./notification-bell";

export function Header() {
  const { t } = useTranslation();
  const user = useSessionStore(state => state.user);
  const { signOut, isPending } = useLogout();
  const [confirming, setConfirming] = useState(false);

  const initials = (user?.name ?? "")
    .split(" ")
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <header className="bg-background/80 border-foreground/5 sticky top-0 z-30 flex h-16 items-center gap-2 border-b px-4 backdrop-blur-xl sm:px-6">
      <SidebarTrigger />
      <Separator orientation="vertical" className="mr-1 h-5" />

      <div className="ml-auto flex items-center gap-1">
        {/* Qo'ng'iroqcha til va mavzudan CHAPDA: u ish quroli, ular
            esa sozlama. O'ng chetdagi avatar yonida turgani uchun
            moderator uni qidirmaydi. */}
        <NotificationBell />
        <LanguageSwitcher />
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <Avatar className="size-8">
                {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
                <AvatarFallback className="text-xs">{initials || "Q"}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="space-y-1">
              <p className="truncate">{user?.name}</p>
              {/* Huquq darajasi ko'rinib turadi: odam nima uchun ba'zi
                  tugmalarni ko'rmayotganini o'zi tushunadi. Rol nomi
                  emas — bir odamda bir nechta rol bo'lishi mumkin va
                  ularning birlashmasi muhim, ro'yxati emas. */}
              {user && (
                <Badge variant="secondary" className="font-normal">
                  {user.isSuperadmin
                    ? t("auth.superadmin")
                    : t("auth.permissionCount", { count: user.permissions.length })}
                </Badge>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {/* Chiqish qaytarib bo'ladi, lekin ishni to'xtatadi: bir
                bosishda ochiq forma ham, filtrlar ham yo'qoladi. */}
            <DropdownMenuItem
              variant="destructive"
              onSelect={event => {
                event.preventDefault();
                setConfirming(true);
              }}
            >
              <LogOut className="size-4" />
              {t("common.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("common.logoutTitle")}
        description={t("common.logoutBody")}
        confirmLabel={t("common.logout")}
        loading={isPending}
        onConfirm={() => void signOut()}
      />
    </header>
  );
}
